import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// Debug diagnostics for Verified Access testing in kiosk mode + Assessment Lock sessions, where dev tools are unavailable
export const VERIFIED_ACCESS_DEBUG_PANEL_ENABLED = true;
const DEBUG_SESSION_STORAGE_KEY = 'verified_access_debug_panel_enabled';

// Sanitized state
export interface VerifiedAccessDebugState {
  route?: string;
  lastUpdatedAt?: string;
  testSessionId?: number | string;
  testAttemptId?: number | string | null;
  extensionStatus?: string;
  extensionAvailable?: boolean;
  verifiedAccessSigningStatus?: string;
  platformKeysAvailable?: boolean | null;
  challengeKeyAvailable?: boolean | null;
  challengeCreateStatus?: string;
  attestationId?: string;
  attestationExpiresIn?: number;
  extensionSignStatus?: string;
  challengeResponseLength?: number;
  challengeVerifyStatus?: string;
  deviceTrustTokenReceived?: boolean;
  deviceTrustTokenLength?: number;
  deviceTrustTokenExpiresIn?: number;
  deviceTrustTokenIssuedAt?: number;
  trustMode?: string;
  softLockActive?: boolean;
  softLockStatus?: string;
  softLockSource?: string;
  softLockMode?: string | null;
  softLockRuntime?: string | null;
  softLockBypassReason?: string | null;
  finalMode?: 'managed_kiosk' | 'byod_extension' | 'blocked' | 'unknown';
  fallbackKioskPasswordSent?: boolean;
  loadAttemptStatus?: string;
  loadAttemptError?: string;
  apiDebug?: any;
  lastError?: string;
}

export interface VerifiedAccessChallengeDebugResponse {
  attestation_id?: string;
  expires_in?: number;
  debug?: any;
}

export interface VerifiedAccessTrustDebugResponse {
  device_trust_token?: string;
  expires_in?: number;
  trust_mode?: string;
  debug?: any;
}

@Injectable({
  providedIn: 'root'
})
export class VerifiedAccessDebugService {
  // This service is provided at root so debug state survives dashboard -> assessment route changes.
  private stateSubject = new BehaviorSubject<VerifiedAccessDebugState>({
    finalMode: 'unknown',
    deviceTrustTokenReceived: false,
    fallbackKioskPasswordSent: false
  });
  private enabledSubject = new BehaviorSubject<boolean>(this.wasActivatedThisSession());

  state$ = this.stateSubject.asObservable();
  enabled$ = this.enabledSubject.asObservable();

  get currentState(): VerifiedAccessDebugState {
    return this.stateSubject.value;
  }

  isEnabled(): boolean {
    return this.enabledSubject.value;
  }

  enable(): void {
    if (!VERIFIED_ACCESS_DEBUG_PANEL_ENABLED) {
      return;
    }
    this.enabledSubject.next(true);
    try {
      // Keep the panel unlocked for the current tab/session after the tester types the secret word
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(DEBUG_SESSION_STORAGE_KEY, '1');
      }
    } catch (e) {}
    this.update({ lastError: null });
  }

  disable(): void {
    this.enabledSubject.next(false);
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(DEBUG_SESSION_STORAGE_KEY);
      }
    } catch (e) {}
  }

  update(partial: VerifiedAccessDebugState): void {
    if (!VERIFIED_ACCESS_DEBUG_PANEL_ENABLED) {
      return;
    }
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partial,
      lastUpdatedAt: new Date().toISOString()
    });
  }

  reset(): void {
    this.stateSubject.next({
      finalMode: 'unknown',
      deviceTrustTokenReceived: false,
      fallbackKioskPasswordSent: false,
      lastUpdatedAt: new Date().toISOString()
    });
  }

  resetForAttestation(testSessionId: number | string): void {
    // Reset the per-attempt details before a new Google challenge/sign/verify flow starts
    this.update({
      testSessionId,
      verifiedAccessSigningStatus: 'pending',
      platformKeysAvailable: null,
      challengeKeyAvailable: null,
      challengeCreateStatus: 'not_started',
      attestationId: null,
      attestationExpiresIn: null,
      extensionSignStatus: 'not_started',
      challengeResponseLength: null,
      challengeVerifyStatus: 'not_started',
      deviceTrustTokenReceived: false,
      deviceTrustTokenLength: null,
      deviceTrustTokenExpiresIn: null,
      deviceTrustTokenIssuedAt: null,
      trustMode: null,
      softLockActive: null,
      softLockStatus: null,
      softLockSource: null,
      softLockMode: null,
      softLockRuntime: null,
      softLockBypassReason: null,
      testAttemptId: null,
      finalMode: 'unknown',
      fallbackKioskPasswordSent: false,
      loadAttemptStatus: 'not_started',
      loadAttemptError: null,
      apiDebug: null,
      lastError: null
    });
  }

  markExtensionStatus(extensionStatus: string, extensionAvailable: boolean): void {
    this.update({
      extensionStatus,
      extensionAvailable
    });
  }

  markReusedManagedKioskTrust(
    testSessionId: number | string,
    extensionStatus: string,
    extensionAvailable: boolean,
    deviceTrustToken: string,
    trustMode: string,
    expiresIn: number
  ): void {
    this.update({
      testSessionId,
      extensionStatus,
      extensionAvailable,
      verifiedAccessSigningStatus: 'skipped',
      challengeCreateStatus: 'skipped',
      extensionSignStatus: 'skipped',
      challengeVerifyStatus: 'skipped',
      deviceTrustTokenReceived: true,
      deviceTrustTokenLength: deviceTrustToken.length,
      deviceTrustTokenExpiresIn: expiresIn,
      deviceTrustTokenIssuedAt: Date.now(),
      trustMode,
      finalMode: 'managed_kiosk',
      lastError: null
    });
  }

  markExtensionUnavailable(): void {
    this.update({
      challengeCreateStatus: 'skipped',
      extensionSignStatus: 'skipped',
      challengeVerifyStatus: 'skipped',
      lastError: 'VERIFIED_ACCESS_EXTENSION_UNAVAILABLE'
    });
  }

  markSigningStatusSuccess(signingStatus: any): void {
    this.update({
      verifiedAccessSigningStatus: 'success',
      platformKeysAvailable: !!signingStatus.platformKeysAvailable,
      challengeKeyAvailable: !!signingStatus.challengeKeyAvailable
    });
  }

  markSigningUnavailable(error?: string): void {
    this.update({
      challengeCreateStatus: 'skipped',
      extensionSignStatus: 'skipped',
      challengeVerifyStatus: 'skipped',
      lastError: error || 'VERIFIED_ACCESS_SIGNING_UNAVAILABLE'
    });
  }

  markSigningStatusUnknown(error: unknown): void {
    this.update({
      verifiedAccessSigningStatus: 'unknown',
      platformKeysAvailable: null,
      challengeKeyAvailable: null,
      lastError: this.errorMessage(error, 'VERIFIED_ACCESS_SIGNING_STATUS_UNKNOWN')
    });
  }

  markSigningStatusTimeout(): void {
    this.update({
      verifiedAccessSigningStatus: 'failed',
      platformKeysAvailable: null,
      challengeKeyAvailable: null,
      challengeCreateStatus: 'skipped',
      lastError: 'VERIFIED_ACCESS_SIGNING_STATUS_TIMEOUT'
    });
  }

  markChallengeCreatePending(): void {
    this.update({ challengeCreateStatus: 'pending' });
  }

  markChallengeCreated(attestation: VerifiedAccessChallengeDebugResponse): void {
    this.update({
      challengeCreateStatus: 'success',
      attestationId: attestation.attestation_id,
      attestationExpiresIn: attestation.expires_in,
      apiDebug: attestation.debug || null
    });
  }

  markChallengeCreateFailed(error: unknown): void {
    this.update({
      challengeCreateStatus: 'failed',
      lastError: this.errorMessage(error, 'VERIFIED_ACCESS_CHALLENGE_CREATE_FAILED')
    });
  }

  markExtensionSignPending(): void {
    this.update({ extensionSignStatus: 'pending' });
  }

  markExtensionSigned(challengeResponse: string): void {
    this.update({
      extensionSignStatus: 'success',
      challengeResponseLength: challengeResponse ? challengeResponse.length : 0
    });
  }

  markExtensionSignFailed(error: unknown): void {
    this.update({
      extensionSignStatus: 'failed',
      challengeVerifyStatus: 'skipped',
      lastError: this.errorMessage(error, 'VERIFIED_ACCESS_EXTENSION_FAILED')
    });
  }

  markChallengeVerifyPending(): void {
    this.update({ challengeVerifyStatus: 'pending' });
  }

  markChallengeVerified(
    verified: VerifiedAccessTrustDebugResponse,
    issuedAtMs: number,
    apiDebug?: any
  ): void {
    this.update({
      challengeVerifyStatus: 'success',
      deviceTrustTokenReceived: !!verified.device_trust_token,
      deviceTrustTokenLength: verified.device_trust_token ? verified.device_trust_token.length : 0,
      deviceTrustTokenExpiresIn: verified.expires_in,
      deviceTrustTokenIssuedAt: issuedAtMs,
      trustMode: verified.trust_mode,
      finalMode: 'managed_kiosk',
      apiDebug
    });
  }

  markChallengeVerifyFailed(error: unknown): void {
    this.update({
      challengeVerifyStatus: 'failed',
      lastError: this.errorMessage(error, 'VERIFIED_ACCESS_CHALLENGE_VERIFY_FAILED')
    });
  }

  markLoadAttemptPreparing(
    testSessionId: number | string,
    extensionStatus: string,
    extensionAvailable: boolean
  ): void {
    this.update({
      testSessionId,
      extensionStatus,
      extensionAvailable,
      loadAttemptStatus: 'preparing'
    });
  }

  markByodFallback(error: unknown): void {
    this.update({
      finalMode: 'byod_extension',
      fallbackKioskPasswordSent: true,
      lastError: this.errorMessage(error, 'VERIFIED_ACCESS_FAILED')
    });
  }

  markLoadAttemptPending(deviceTrustToken: string | null, kioskPassword?: string): void {
    this.update({
      fallbackKioskPasswordSent: kioskPassword === 'EXTENSION_DETECTED',
      finalMode: deviceTrustToken ? 'managed_kiosk' : (kioskPassword === 'EXTENSION_DETECTED' ? 'byod_extension' : 'unknown'),
      loadAttemptStatus: 'pending'
    });
  }

  markLoadAttemptFetched(): void {
    this.update({
      loadAttemptStatus: 'fetched'
    });
  }

  markLoadAttemptSuccess(): void {
    this.update({
      loadAttemptStatus: 'success'
    });
  }

  markLoadAttemptBlocked(error: string): void {
    this.update({
      finalMode: 'blocked',
      loadAttemptStatus: 'failed',
      loadAttemptError: error
    });
  }

  markLoadAttemptFailed(error: unknown): void {
    this.update({
      loadAttemptStatus: 'failed',
      loadAttemptError: this.errorMessage(error, 'LOAD_ATTEMPT_FAILED')
    });
  }

  markSoftlockStatus(
    active: boolean,
    status: string,
    source?: string,
    details?: {
      testAttemptId?: number | string | null,
      softLockMode?: string | null,
      softLockRuntime?: string | null,
      softLockBypassReason?: string | null,
    }
  ): void {
    this.update({
      softLockActive: active,
      softLockStatus: status,
      softLockSource: source || null,
      testAttemptId: details?.testAttemptId || null,
      softLockMode: details?.softLockMode || null,
      softLockRuntime: details?.softLockRuntime || null,
      softLockBypassReason: details?.softLockBypassReason || null
    });
  }

  // no raw tokens
  sanitizedDump(): VerifiedAccessDebugState {
    return {
      ...this.currentState,
      deviceTrustTokenReceived: !!this.currentState.deviceTrustTokenReceived
    };
  }

  private errorMessage(error: unknown, fallback: string): string {
    return error && typeof error === 'object' && 'message' in error ? `${(error as any).message}` : fallback;
  }

  private wasActivatedThisSession(): boolean {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DEBUG_SESSION_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
}
